/**
 * Fenetres modales. Le contrat clavier vient de `useCoucheModale` ; le rendu
 * passe par un portail pour echapper aux contextes d'empilement de la page
 * (barre superieure collante, zones defilantes).
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Bouton, BoutonIcone } from './boutons';
import { useCoucheModale } from './couche';
import type { OptionsFocusCouche } from './couche';
import { MessageErreur } from './etats';

export function Modale({
  titre,
  description,
  ouverte,
  onFermer,
  children,
  pied,
  largeur = 'max-w-lg',
  verrouillee = false,
  role = 'dialog',
  refFocusInitial,
  refFocusRetour,
}: OptionsFocusCouche & {
  titre: string;
  description?: ReactNode;
  ouverte: boolean;
  onFermer: () => void;
  children?: ReactNode;
  pied?: ReactNode;
  largeur?: string;
  /**
   * Retire toutes les sorties implicites (croix, Echap, clic sur le fond).
   * Reserve au cas ou fermer par megarde ferait perdre une information
   * irrecuperable : la seule issue est alors une action explicite du pied.
   */
  verrouillee?: boolean;
  /** `alertdialog` pour une demande de confirmation. */
  role?: 'dialog' | 'alertdialog';
}) {
  const refContenu = useRef<HTMLDivElement>(null);
  const idTitre = useId();
  const idDescription = useId();

  useCoucheModale({
    ouverte,
    refConteneur: refContenu,
    onEchap: verrouillee ? undefined : onFermer,
    refFocusInitial,
    refFocusRetour,
  });

  if (!ouverte) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        aria-hidden="true"
        className="fixed inset-0 animate-fondu bg-neutre-950/45"
        onClick={verrouillee ? undefined : onFermer}
      />
      {/* Gabarit au moins aussi haut que l'ecran et qui grandit avec la
          fenetre. Calee en bas ou centree directement dans le conteneur
          defilant, une fenetre plus haute que l'ecran deborderait vers le
          haut, zone que le defilement n'atteint pas : son en-tete serait coupe. */}
      <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
        <div
          ref={refContenu}
          role={role}
          aria-modal="true"
          aria-labelledby={idTitre}
          aria-describedby={description ? idDescription : undefined}
          tabIndex={-1}
          className={`relative z-10 w-full animate-apparition rounded-xl border border-bordure bg-surface shadow-flottant ${largeur}`}
        >
          <header className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
            <div className="min-w-0">
              <h2 id={idTitre} className="text-lg font-semibold tracking-titre text-encre">
                {titre}
              </h2>
              {description && (
                <div id={idDescription} className="mt-1 text-sm text-encre-douce">
                  {description}
                </div>
              )}
            </div>
            {!verrouillee && (
              <BoutonIcone
                libelle="Fermer la fenêtre"
                icone={X}
                onClick={onFermer}
                className="-mt-1 -mr-2 shrink-0"
              />
            )}
          </header>
          {children && <div className="px-5 pb-5">{children}</div>}
          {pied && (
            /* Le panneau ne rogne pas ses enfants (un menu deroulant doit pouvoir
               en deborder) : le pied arrondit lui-meme ses coins, sinon son fond
               depasse du rayon du panneau. */
            <footer className="flex flex-wrap justify-end gap-2 rounded-b-xl border-t border-bordure bg-surface-douce px-5 py-3">
              {pied}
            </footer>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Demande de confirmation avant une action irreversible (suppression,
 * annulation). `onConfirmer` peut etre asynchrone : la fenetre attend sa fin,
 * se ferme si elle reussit, et reste ouverte avec l'erreur si elle echoue.
 */
export function Confirmation({
  ouverte,
  titre,
  message,
  libelleConfirmer,
  libelleAnnuler = 'Annuler',
  ton = 'danger',
  onConfirmer,
  onFermer,
  children,
  refFocusInitial,
  refFocusRetour,
  messageErreur,
}: OptionsFocusCouche & {
  ouverte: boolean;
  titre: string;
  message: ReactNode;
  libelleConfirmer: string;
  libelleAnnuler?: string;
  ton?: 'danger' | 'normal';
  onConfirmer: () => void | Promise<unknown>;
  onFermer: () => void;
  children?: ReactNode;
  /** Phrase d'un echec de `onConfirmer` (`MessageErreur`) ; a defaut, celle de l'API. */
  messageErreur?: (erreur: unknown) => string;
}) {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);

  // Une confirmation rouverte ne doit pas afficher l'echec de la precedente.
  useEffect(() => {
    if (ouverte) setErreur(null);
  }, [ouverte]);

  const confirmer = async (): Promise<void> => {
    setErreur(null);
    setEnCours(true);
    try {
      await onConfirmer();
      setEnCours(false);
      onFermer();
    } catch (cause) {
      setEnCours(false);
      setErreur(cause);
    }
  };

  return (
    <Modale
      titre={titre}
      description={message}
      ouverte={ouverte}
      onFermer={onFermer}
      role="alertdialog"
      largeur="max-w-md"
      verrouillee={enCours}
      refFocusInitial={refFocusInitial}
      refFocusRetour={refFocusRetour}
      pied={
        <>
          <Bouton onClick={onFermer} disabled={enCours}>
            {libelleAnnuler}
          </Bouton>
          <Bouton
            variante={ton === 'danger' ? 'danger' : 'primaire'}
            chargement={enCours}
            onClick={() => void confirmer()}
          >
            {libelleConfirmer}
          </Bouton>
        </>
      }
    >
      {children || erreur ? (
        <div className="space-y-3">
          {children}
          {erreur ? <MessageErreur erreur={erreur} titre="L'action n'a pas abouti" message={messageErreur} /> : null}
        </div>
      ) : null}
    </Modale>
  );
}
